"use client"

import styled from "styled-components"
import { Sparkles, ChevronRight, User, Play } from "lucide-react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.textPrimary};
`

const ViewAllLink = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.buttonPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`

const PostsList = styled.div`
  display: flex;
  flex-direction: column;
`

const PostItem = styled.div`
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const PostThumbnail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.borderLight};
  
  position: relative;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const VideoPlayBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const PostInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const PostTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${props => props.theme.textPrimary};
`

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const PostAuthor = styled.span`
  color: ${props => props.theme.buttonPrimary};
`

const LoadingIndicator = styled.div`
  padding: 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`

// 날짜 형식 변환 함수
const formatDate = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffDay === 0) return "오늘";
  if (diffDay === 1) return "어제";
  if (diffDay < 7) return `${diffDay}일 전`;
  
  return `${date.getMonth()+1}월 ${date.getDate()}일`;
};

export default function RecommendedPosts() {
  const { theme } = useTheme()
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  console.log('RecommendedPosts 렌더링됨, posts 개수:', posts.length);

  useEffect(() => {
    const fetchRecommendedPosts = async () => {
      try {
        setLoading(true);
        
        // 추천 게시물 가져오기
        const response = await fetch('/post/recommended-posts?limit=4', {
          credentials: 'include'
        });
        
        const data = await response.json();
        console.log("추천 게시물 데이터:", data);
        
        if (data.success && data.posts) {
          setPosts(data.posts);
        }
      } catch (error) {
        console.error("추천 게시물 로드 오류:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendedPosts();
  }, []);

  // 샘플 게시물 데이터
  const samplePosts = [
    {
      id: 1,
      content: "Next.js와 React로 만드는 모던 웹 애플리케이션",
      author_username: "frontend_dev",
      created_at: new Date(Date.now() - 0).toISOString(),
      images: [{url: "/placeholder.svg?height=60&width=60"}],
    },
    {
      id: 2,
      content: "개발자를 위한 효율적인 협업 도구 TOP 5",
      author_username: "teamlead",
      created_at: new Date(Date.now() - 86400000).toISOString(), // 1일 전
      images: [{url: "/placeholder.svg?height=60&width=60"}],
    },
    {
      id: 3,
      content: "디자인 시스템 구축하기: 실전 가이드",
      author_username: "ux_designer",
      created_at: new Date(Date.now() - 2*86400000).toISOString(), // 2일 전
      images: [{url: "/placeholder.svg?height=60&width=60"}],
    },
    {
      id: 4,
      content: "클라우드 서비스 비교: AWS vs Azure vs GCP",
      author_username: "cloud_expert",
      created_at: new Date(Date.now() - 3*86400000).toISOString(), // 3일 전
      images: [{url: "/placeholder.svg?height=60&width=60"}],
    },
  ];

  // API 호출 결과가 없으면 샘플 데이터 사용
  const displayPosts = posts.length > 0 ? posts : samplePosts;

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          <Sparkles size={18} />
          추천 게시물
        </Title>
        <ViewAllLink theme={theme} onClick={() => navigate('/recommended')}>
          더보기
          <ChevronRight size={16} />
        </ViewAllLink>
      </Header>
      
      {loading ? (
        <LoadingIndicator theme={theme}>추천 게시물을 불러오는 중...</LoadingIndicator>
      ) : (
        <PostsList>
          {displayPosts.map((post) => {
            // 이미지 처리 - JSON 문자열이거나 배열일 수 있음
            let imageUrl = null;
            let isVideo = false;
            if (post.images) {
              let images = post.images;
              // JSON 문자열인 경우 파싱
              if (typeof images === 'string') {
                try {
                  images = JSON.parse(images);
                } catch (e) {
                  console.error('이미지 파싱 오류:', e);
                }
              }
              // 배열이고 첫 번째 요소가 있는 경우
              if (Array.isArray(images) && images.length > 0) {
                const firstImage = images[0];
                imageUrl = typeof firstImage === 'object' ? (firstImage.url || firstImage.image_url) : firstImage;
                // 영상 파일 확인
                if (imageUrl && /\.(mp4|mov|webm|avi)(\?|$)/i.test(imageUrl)) {
                  isVideo = true;
                }
              }
            }
            
            return (
              <PostItem key={post.id} theme={theme} onClick={() => navigate(`/posts/${post.id}`)}>
                <PostThumbnail theme={theme}>
                  {imageUrl && isVideo ? (
                    <>
                      <video
                        src={imageUrl}
                        muted
                        preload="metadata"
                        onLoadedData={(e) => { e.target.currentTime = 0.5; }}
                      />
                      <VideoPlayBadge><Play size={12} color="white" fill="white" /></VideoPlayBadge>
                    </>
                  ) : imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="게시물 썸네일"
                      onError={(e) => {
                        if (!e.target.dataset.tried) {
                          e.target.dataset.tried = 'true';
                          e.target.src = "/placeholder.svg?height=60&width=60";
                        }
                      }}
                    />
                  ) : (
                    <User size={24} color={theme.textMuted} />
                  )}
                </PostThumbnail>
              <PostInfo>
                <PostTitle theme={theme}>{post.content}</PostTitle>
                <PostMeta theme={theme}>
                  <div>
                    <PostAuthor theme={theme}>@{post.author_username}</PostAuthor> • {formatDate(post.created_at)}
                  </div>
                  <div>조회 {post.view_count || 0}</div>
                </PostMeta>
              </PostInfo>
            </PostItem>
            );
          })}
        </PostsList>
      )}
    </Container>
  );
}